import { generators, validators, generateGameSaveStorageRequest, GameSaveStorageMessageType } from '@playcastdotio/Messaging';
import type { HostMessage, NativeGameSaveStorageReply, NativeGameSaveStorageRequest } from '@playcastdotio/Messaging';
import type { IRealtimeService } from '../realtime/IRealtimeService';
import type { IMessageRouter } from '../MessageRouter';
import { USE_MESSAGE_ROUTER, USE_PAVILION_COORDINATOR, USE_PLAYJECTOR_COORDINATOR } from '../../config/featureFlags';
import type { IIpcService } from '../ipc/IIpcService';

export const GAME_SAVE_STORAGE_REQUEST_CHANNEL = 'game-save:storage-request';
export interface IGameSaveStorageRequestChannel {
  subscribeStorageRequests(listener: (message: unknown) => void): () => void;
}
interface RendererMessageBridge { on(channel: string, callback: (message: unknown) => void): void }

/** Dedicated preload channel preserves the existing mainMessage listener. */
export class GameSaveStorageRequestChannel implements IGameSaveStorageRequestChannel {
  private readonly listeners = new Set<(message: unknown) => void>();
  constructor(bridge?: RendererMessageBridge) {
    bridge?.on(GAME_SAVE_STORAGE_REQUEST_CHANNEL, (message) => {
      for (const listener of this.listeners) listener(message);
    });
  }
  subscribeStorageRequests(listener: (message: unknown) => void): () => void {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  }
}

export interface IGameSaveStorageTransport {
  sendStorageRequest(request: NativeGameSaveStorageRequest): boolean;
  subscribeStorageReplies(listener: (reply: NativeGameSaveStorageReply) => void): () => void;
  subscribeDisconnected(listener: () => void): () => void;
}
interface StorageDependencies {
  requests: IGameSaveStorageRequestChannel; transport: IGameSaveStorageTransport;
  ipcService: IIpcService; hostIdentity: { getHostAuthId(): string }; timeoutMs?: number;
}
interface PendingStorageRequest {
  request: NativeGameSaveStorageRequest; timeout: ReturnType<typeof setTimeout>;
}

/** Relays signed native tickets only; save files and storage credentials stay outside the renderer. */
export class GameSaveStorageMessageService {
  private readonly pending = new Map<string, PendingStorageRequest>();
  private readonly unsubscribe: Array<() => void> = [];
  private destroyed = false;
  constructor(private readonly dependencies: StorageDependencies) {
    this.unsubscribe.push(dependencies.requests.subscribeStorageRequests((message) => this.forwardRequest(message)),
      dependencies.transport.subscribeStorageReplies((reply) => this.receiveReply(reply)),
      dependencies.transport.subscribeDisconnected(() => this.failPending('transfer_failed')));
  }
  destroy(): void {
    this.destroyed = true;
    for (const unsubscribe of this.unsubscribe) unsubscribe();
    this.failPending('transfer_failed');
  }
  private forwardRequest(message: unknown): void {
    const envelope = validators.gameSave.validateGameSaveStorageMessage(message);
    if (this.destroyed || !envelope || envelope.header.isReply) return;
    const request = envelope.body.message as NativeGameSaveStorageRequest;
    if (this.pending.has(request.requestId)) return;
    if (this.pending.size >= 1) {
      this.sendFailure(request, 'conflict');
      return;
    }
    const timeout = setTimeout(() => this.finish(request.requestId, {
      requestId: request.requestId, action: request.action, status: 'failed',
      error: { code: 'deadline_exceeded', retryable: true, message: 'Game save storage ticket timed out.' },
    }), this.dependencies.timeoutMs ?? 25000);
    this.pending.set(request.requestId, { request, timeout });
    try {
      if (!this.dependencies.transport.sendStorageRequest(request)) this.finishWithFailure(request, 'transfer_failed');
    } catch { this.finishWithFailure(request, 'transfer_failed'); }
  }
  private receiveReply(reply: NativeGameSaveStorageReply): void {
    const pending = this.pending.get(reply.requestId);
    if (!pending || reply.action !== pending.request.action ||
      !validators.gameSave.gameSaveStorageReplySchema.safeParse(reply).success) return;
    this.finish(reply.requestId, reply);
  }
  private finish(requestId: string, reply: NativeGameSaveStorageReply): void {
    const pending = this.pending.get(requestId);
    if (!pending) return;
    clearTimeout(pending.timeout);
    this.pending.delete(requestId);
    this.sendReply(reply);
  }
  private finishWithFailure(request: NativeGameSaveStorageRequest, code: 'transfer_failed'): void {
    this.finish(request.requestId, this.failure(request, code));
  }
  private failPending(code: 'transfer_failed'): void {
    for (const { request } of this.pending.values()) this.finishWithFailure(request, code);
  }
  private failure(request: NativeGameSaveStorageRequest, code: 'transfer_failed' | 'conflict'): NativeGameSaveStorageReply {
    return { requestId: request.requestId, action: request.action, status: 'failed',
      error: { code, retryable: true, message: 'Game save storage connection is unavailable.' } };
  }
  private sendFailure(request: NativeGameSaveStorageRequest, code: 'conflict'): void {
    this.sendReply(this.failure(request, code));
  }
  private sendReply(reply: NativeGameSaveStorageReply): void {
    try {
      this.dependencies.ipcService.send(generators.gameSave.gameSaveStorageReply(
        this.dependencies.hostIdentity.getHostAuthId(), reply));
    } catch { /* The native deadline handles a disconnected Electron process. */ }
  }
}

/** Coordinator-scoped transport: signed requests use authenticated immediate sends only. */
export class GameSaveStorageRealtimeChannel implements IGameSaveStorageTransport {
  private readonly replies = new Set<(reply: NativeGameSaveStorageReply) => void>();
  private readonly disconnected = new Set<() => void>();
  private readonly unsubscribeDisconnected: () => void;
  private destroyed = false;
  constructor(private readonly realtime: IRealtimeService,
    private readonly enabled = USE_MESSAGE_ROUTER && USE_PAVILION_COORDINATOR && USE_PLAYJECTOR_COORDINATOR) {
    this.unsubscribeDisconnected = enabled ? realtime.onDisconnected(() => {
      for (const listener of this.disconnected) listener();
    }) : () => undefined;
  }
  registerMessageHandlers(router: IMessageRouter): void {
    if (this.enabled) router.register(GameSaveStorageMessageType.HOST_GAME_SAVE_STORAGE_REPLY,
      (message) => { this.handleStorageMessage(message as HostMessage); });
  }
  sendStorageRequest(request: NativeGameSaveStorageRequest): boolean {
    if (!this.enabled || this.destroyed || !this.realtime.isConnected()) return false;
    return this.realtime.trySendMessage(generateGameSaveStorageRequest(request));
  }
  handleStorageMessage(message: HostMessage): boolean {
    if (!this.enabled || this.destroyed || message.type !== GameSaveStorageMessageType.HOST_GAME_SAVE_STORAGE_REPLY) return false;
    if (this.realtime.isConnected()) for (const listener of this.replies) listener(message.reply);
    return true;
  }
  subscribeStorageReplies(listener: (reply: NativeGameSaveStorageReply) => void): () => void {
    this.replies.add(listener);
    return () => { this.replies.delete(listener); };
  }
  subscribeDisconnected(listener: () => void): () => void {
    this.disconnected.add(listener);
    return () => { this.disconnected.delete(listener); };
  }
  destroy(): void {
    this.destroyed = true;
    for (const listener of this.disconnected) listener();
    this.unsubscribeDisconnected();
    this.replies.clear();
    this.disconnected.clear();
  }
}
